import React, { useEffect, useState, forwardRef, useRef } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  makeStyles,
  TableContainer,
  Divider,
  Checkbox
} from "@material-ui/core";

import { getComparator, getValueObject, stableSort } from "../../../helpers/tables";
import DeleteButtonAbm from "../../compartidos/abms/DeleteButtonAbm";

const useStyles = makeStyles({
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
});

export const GenericTable = React.forwardRef(({
  data,
  columns,
  sizeTableCells,
  withoutPagination,
  tituloSinResultado,
  align = "left",
  rowStyle = {},
  colTitleStyle = {},
  tituloSinResultadoStyle = {},
  withCheckBox = false,
  handleSelectAll = () => { },
  handleSelect = () => { },
  selected = [],
  rowPerPage =  10,
  disabledCheck,
  withDeleteButton = false
}, ref) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowPerPage);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState();

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [data]);

  return (
    <>
      <TableContainer>
        <Table ref={ref}>
          <TableHead>
            <TableRow>
              {withCheckBox ? (
                <TableCell padding="checkbox" align="center">
                  <Checkbox
                    checked={data?.length > 0 && selected?.length === data?.length}
                    indeterminate={selected.length > 0 && selected.length < data?.length}
                    value={data?.length > 0 && selected?.length === data?.length}
                    onClick={handleSelectAll}
                    inputProps={{ "aria-label": "select all desserts" }}
                    disabled={data?.length === 0 || disabledCheck}
                  />
                </TableCell>
              ) : null}
              {columns.filter(({filter}) => !filter).map((c, index) => {
                if (c.sort) {
                  return (
                    <TableCell
                      key={index}
                      sortDirection={orderBy === c.id ? order : false}
                      align={align}
                      style={{
                        minWidth: c.minWidth || 'auto',
                        ...colTitleStyle
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === c.id}
                        direction={orderBy === c.id ? order : "asc"}
                        onClick={() => { debugger; handleRequestSort(c.id) }}
                      >
                        {c.label}
                        {orderBy === c.id ? (
                          <span className={classes.visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  );
                }
                return (
                  <TableCell
                    key={index}
                    align={align}
                    style={{
                      minWidth: c.minWidth || 'auto',
                      ...colTitleStyle
                    }}
                  >
                    {c.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {(orderBy ? stableSort(data, getComparator(order, orderBy)) : data)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item, index) => {
                return (
                  <TableRow hover key={index}>
                    {withCheckBox ? (
                      <TableCell padding="checkbox" align="center">
                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 0 }}>
                          {withDeleteButton && withDeleteButton?.condition(item) && (
                            <DeleteButtonAbm
                              handleDelete={() => withDeleteButton.deleteProps.handleDelete(item)}
                              permiso={withDeleteButton.deleteProps.permiso}
                              mensajeEliminacion={withDeleteButton.deleteProps.mensajeEliminacion}
                              motivo={withDeleteButton.deleteProps.motivoBaja}
                              setMotivo={withDeleteButton.deleteProps.setMotivoBaja}
                              validators={withDeleteButton.deleteProps.validators}
                              errorMessages={withDeleteButton.deleteProps.errorMessages}
                            />
                          )}
                          <Checkbox
                            checked={selected.indexOf(item.id) !== -1}
                            inputProps={{ 'aria-labelledby': item.id }}
                            onClick={(e) => handleSelect(e, item.id)}
                            disabled={disabledCheck}
                          />
                        </div>
                      </TableCell>
                    ) : null}
                    {columns.filter(({filter}) => !filter).map((c, i) => {
                      return (
                        <TableCell
                          align={align}
                          key={i}
                          style={{
                            width: `${sizeTableCells
                              ? sizeTableCells[i]
                              : ""
                              }`,
                            ...rowStyle
                          }}
                        >
                          {c.customBody
                            ? c.customBody(item, i)
                            : getValueObject(c.id, item)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        {data.length === 0 && (
          <>
            <div style={{ ...tituloSinResultadoStyle, textAlign: "center", margin: "1.5rem 0" }}>
              {tituloSinResultado ? tituloSinResultado : 'No hay resultados para su búsqueda'}
            </div>
            <Divider />
          </>
        )}
      </TableContainer>

      {withoutPagination ||
        <TablePagination
          component="div"
          count={data.length}
          rowsPerPageOptions={[10, 50, 100]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      }
    </>
  );
});

export const GenericCheckboxTable = forwardRef(({ selected, setSelected, ...props }, ref) => {
  const componentRef = useRef();

  const handleSelectAll = (e) => {
    if (e.target.checked && selected.length === 0) {
      setSelected(props.data.map((item) => item.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (e, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };

  return (
    <div ref={ref}>
      <GenericTable
        ref={componentRef}
        selected={selected}
        handleSelect={handleSelect}
        handleSelectAll={handleSelectAll}
        withCheckBox
        {...props}
      />
    </div>
  );
});